import { RuleModel } from "@syncfusion/ej2-react-querybuilder";
import { Fragment, useEffect, useState } from "react";
import { useAsync } from "react-async";
import { useHistory, useParams } from "react-router-dom";
import ProofingRuleScope from "../../../api/models/proofingRuleScope";
import { addEditProofingRule, deleteProofingRule, getProofingRuleById, getProofingScopes } from "../../../api/services/proofingRulesService";
import Layout from "../../../components/layout";
import { ResponseStatus } from "../../../components/notifications";
import ProofingRuleEditor from "../../../components/proofingRuleEditor";
import SubmitButton from "../../../components/submitButton";
import RuleDebugger from "./edit.rule.debugger";

type Params = {
    proofingId: string;
    id: string;
};

//this prevents the ProofingRuleEditor from refreshing the state of the whole review when the value is changed.
let newRules: RuleModel | undefined;
let ruleName: string;

const EditProofing = () => {
    const history = useHistory();
    const { id } = useParams<Params>();
    const [isLoading, loading] = useState<boolean>(false);
    const [defaultName, setDefaultName] = useState<string>("");
    const [isActive, setActive] = useState<boolean>(true);
    const [scopeId, setScopeId] = useState<number>(1);
    const [rules, setRules] = useState<RuleModel>({});
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [showDebugger, setShowDebugger] = useState<boolean>(false);
    const [scopes, setScopes] = useState<ProofingRuleScope[]>();

    //load the rule if we aren't creating a new one
    useEffect(() => {
        const getRule = async () => {
            loading(true);

            var rule = await getProofingRuleById(Number(id));
            if (typeof rule.data === "undefined")
                return;

            setDefaultName(rule.data.name);
            ruleName = rule.data.name;
            setActive(rule.data.isActive);
            setRules(JSON.parse(rule.data.pql));
            setScopeId(rule.data.scopeId);
            loading(false);
        }

        const loadScopes = async () => {
            var res = await getProofingScopes();
            if (typeof res.data !== "undefined")
                setScopes(res.data);
        }

        loadScopes();

        if (id !== "new")
            getRule();

        newRules = undefined;
    }, [id]);

    const saveChanges = async (args: any[]) => {
        //validation
        if (typeof ruleName === "undefined" || ruleName === "")
            return Promise.reject('Please enter a name.');

        if (ruleName.length >= 200)
            return Promise.reject('The name must be less than 200 characters');

        if (typeof newRules !== "undefined")
            setRules(newRules);

        loading(true);

        await addEditProofingRule({
            proofingRuleId: (id !== "new") ? Number(id).valueOf() : -1,
            name: ruleName,
            pql: (typeof newRules !== "undefined") ? JSON.stringify(newRules) : JSON.stringify(rules),
            scopeId: scopeId,
            isActive: isActive
        });

        loading(false);

        if (typeof args[0].exitOnSave !== "undefined" && args[0].exitOnSave) {
            setTimeout(() => {
                history.replace("/admin/proofing");
            }, 1000);
        }

        return Promise.resolve();
    }
    const saveApi = useAsync({ deferFn: saveChanges });

    const removeRule = async () => {
        loading(true);
        await deleteProofingRule(Number(id).valueOf());
        loading(false);
        return Promise.resolve();
    }
    const removeApi = useAsync({
        deferFn: removeRule,
        onResolve: () => {
            setTimeout(() => {
                history.replace("/admin/proofing");
            }, 1000);
        }
    });

    const scopeChanged = (scopeId: number) => {
        setScopeId(scopeId);
        setRules({});
    }

    return (
        <Layout isLoading={isLoading}>
            <Fragment>
                <ResponseStatus state={saveApi} />
                <ResponseStatus state={removeApi} />

                {!removeApi.isResolved && (
                    <Fragment>
                        <label htmlFor="ruleName" className='block mb-1'>
                            Rule Name *
                        </label>
                        <input
                            type="text"
                            name="ruleName"
                            defaultValue={defaultName ?? ""}
                            onChange={(e) => { ruleName = e.target.value }} />
                        <div>
                            <label>Scope</label>
                            <br />
                            <div className="w-40 inline-block">
                                <select
                                    value={scopeId}
                                    onChange={(e) => { scopeChanged(Number(e.target.value)); }}>
                                    {scopes?.sort((a, b) => {
                                        return a.name.localeCompare(b.name);
                                    }).map((option, i) => (
                                        <option key={"sp_" + i} value={option.proofingRuleScopeId}>{option.name}</option>
                                    ))}
                                </select>
                            </div>
                            <span className="ml-2">
                                note: changing this will clear the rules set below
                            </span>
                        </div>

                        <div className="mt-1">
                            <label className="ml-2">
                                <input
                                    className="mr-1"
                                    type="radio"
                                    name="ruleIsActive"
                                    value="true"
                                    onChange={() => { setActive(true); }}
                                    checked={isActive} />
                                Active
                            </label>

                            <label className="ml-2">
                                <input
                                    className="mr-1"
                                    type="radio"
                                    name="ruleIsActive"
                                    value="false"
                                    onChange={() => { setActive(false); }}
                                    checked={!isActive} />
                                Disabled
                            </label>
                        </div>

                        <ProofingRuleEditor
                            defaultRule={rules}
                            scopeId={scopeId}
                            ruleChanged={(r) => { console.log("RuleUpdated", r); newRules = r; }}
                        />

                        <div className='flex justify-between mt-5'>
                            <SubmitButton
                                className={(id === "new") ? "opacity-25 secondary" : "secondary"}
                                disabled={id === "new"}
                                text='Remove'
                                onClick={() => { removeApi.run(); }} />

                            <div>
                                <SubmitButton
                                    text="Save"
                                    className="inline-block mr-2 bg-ptsblue-normal hover:bg-gray-700"
                                    onClick={() => { setShowDebugger(true); saveApi.run({ exitOnSave: false }); }} />

                                <SubmitButton
                                    text='Save & close'
                                    className="inline-block"
                                    onClick={() => { setShowDebugger(false); saveApi.run({ exitOnSave: true }); }} />
                            </div>
                        </div>

                        <RuleDebugger
                            className="mt-5"
                            ruleId={(id === "new") ? -1 : Number(id)} />
                    </Fragment>
                )}
            </Fragment>
        </Layout>
    )
}

export default EditProofing;